import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MagneticButton from "@/components/ui/MagneticButton";
import TextReveal from "@/components/ui/TextReveal";
import MoodTuner from "@/components/demos/MoodTuner";
import StudentHabitDemo from "@/components/demos/StudentHabitDemo";
import CorporateROI from "@/components/demos/CorporateROI";

export default function HeroSwitcher() {
  const [role, setRole] = useState("individual");

  const roles = [
    { id: "individual", label: "Individual", headline: "One intelligent layer for your human mind." },
    { id: "workplace", label: "Leader", headline: "Prevent burnout before it hits your P&L." },
    { id: "campus", label: "Educator", headline: "Turn student anxiety into resilient habits." },
  ];

  const currentRole = roles.find((r) => r.id === role);

  return (
    <div className="w-full flex flex-col items-center">
      {/* 1. The Switcher Tabs */}
      <div className="flex p-1 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-full mb-8 backdrop-blur-xl relative z-40">
        {roles.map((r) => (
          <button
            key={r.id}
            onClick={() => setRole(r.id)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
              role === r.id
                ? "text-[var(--bg-main)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-main)]"
            }`}
          >
            {role === r.id && (
              <motion.div
                layoutId="activeRole"
                className="absolute inset-0 bg-[var(--text-main)] rounded-full"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            <span className="relative z-10">{r.label}</span>
          </button>
        ))}
      </div>

      {/* 2. The Dynamic Content */}
      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center w-full max-w-7xl px-6 lg:px-10">
        {/* Text Side */}
        <div className="space-y-8 relative z-30 text-center lg:text-left">
          <div className="min-h-[160px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={role}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h1 className="font-heading text-5xl md:text-7xl leading-[1.1] text-[var(--text-main)] tracking-tight">
                  {currentRole.headline}
                </h1>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex flex-wrap justify-center lg:justify-start gap-4">
            <MagneticButton className="px-8 py-4 bg-[var(--nily-teal)] text-[var(--bg-main)]">
              {role === "individual" ? "Start Your Journey" : "View Platform"}
            </MagneticButton>
          </div>
        </div>

        {/* Widget Side */}
        <div className="relative z-20 h-[500px] w-full flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={role}
              initial={{ opacity: 0, scale: 0.9, rotateX: 20 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.9, rotateX: -20 }}
              transition={{ duration: 0.5, type: "spring" }}
              className="w-full flex justify-center"
            >
              {role === "individual" && <MoodTuner />}
              {role === "campus" && <StudentHabitDemo />}
              {role === "workplace" && <CorporateROI />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
